import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { env } from "@/config/env";
import { getAdminAccessToken } from "@/features/auth/session/adminAccessToken";
import {
  ensureFreshAccessTokenForRequest,
  isProtectedApiPath,
  notifyAuthSessionExpired,
  refreshAdminSession,
} from "@/features/auth/session/adminTokenRefresh";
import { parseApiErrorBody, parseApiErrorDetails } from "./parseApiErrorMessage";

export class ApiClientError extends Error {
  readonly errorCode: string | null;
  readonly field: string | null;
  readonly status: number;

  constructor(
    message: string,
    options: {
      errorCode?: string | null;
      field?: string | null;
      status?: number;
    } = {},
  ) {
    super(message);
    this.name = "ApiClientError";
    this.errorCode = options.errorCode ?? null;
    this.field = options.field ?? null;
    this.status = options.status ?? 0;
  }
}

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retryAfterRefresh?: boolean;
};

function errorFromAxiosError(error: AxiosError): ApiClientError {
  const status = error.response?.status ?? 0;
  const statusText = error.response?.statusText ?? "Error";
  const fallback = `Request failed: ${status} ${statusText}`;
  const parsed = parseApiErrorDetails(error.response?.data, fallback);
  return new ApiClientError(parsed.message, {
    errorCode: parsed.errorCode,
    field: parsed.field,
    status,
  });
}

/** Shared Axios instance for admin API calls. */
export const apiClient = axios.create({
  baseURL: env.apiBaseUrl || undefined,
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

apiClient.interceptors.request.use(async (config) => {
  if (isProtectedApiPath(config.url)) {
    const hasToken = await ensureFreshAccessTokenForRequest();
    if (!hasToken && !getAdminAccessToken()) {
      notifyAuthSessionExpired();
      return Promise.reject(new Error("Session expired. Please sign in again."));
    }
  }

  const token = getAdminAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const base = config.baseURL ?? "";
  const url = config.url ?? "";
  if (base.includes("ngrok") || url.includes("ngrok")) {
    config.headers["ngrok-skip-browser-warning"] = "true";
  }

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.info("[api]", `${base}${url}`);
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const axiosError = error as AxiosError;
    const originalRequest = axiosError.config as RetryableRequestConfig | undefined;
    const status = axiosError.response?.status;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retryAfterRefresh &&
      isProtectedApiPath(originalRequest.url)
    ) {
      const refreshed = await refreshAdminSession();
      if (refreshed) {
        const token = getAdminAccessToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        originalRequest._retryAfterRefresh = true;
        return apiClient(originalRequest);
      }

      notifyAuthSessionExpired();
    }

    return Promise.reject(errorFromAxiosError(axiosError));
  },
);
